import {
  formatMovementDelta,
  formatMovementValidUntilShort,
  movementActionLabel,
  movementProgram,
  normalizeOptsFromPassbook,
  normalizePointLogs,
  resolveLogValidUntil,
} from '@/features/leads/model/lead-logs';
import { currency, decimal } from '@/shared/lib/format';
import { passbookCurrency } from '@/shared/lib/passbook-utils';
import type { Passbook } from '@/shared/types';
import type { ParticipantProgram } from '../api/participant-api';

/**
 * O histórico do participante usa as MESMAS regras do extrato do lead no admin
 * (`lead-logs`): rótulo, sinal e delta saem iguais nos dois lados. Elas pedem
 * um `Passbook`; aqui só os campos de programa/moeda/validade importam.
 */
export function programPassbook(idPassbook: string, program: ParticipantProgram): Passbook {
  return { _id: idPassbook, organizationName: '', ...program } as unknown as Passbook;
}

export interface HistoryItem {
  sku?: string;
  name: string;
  quantity?: number;
  /** Preço unitário já formatado. */
  price?: string;
}

export interface HistoryEntry {
  id: string;
  /** ISO. */
  date: string;
  title: string;
  /** "+120 pontos" / "−1 selo" / "+R$ 5,00". */
  delta: string;
  direction: 'in' | 'out' | 'neutral';
  store?: string;
  /** Valor da compra (R$), quando o log traz. */
  price?: string;
  /** "Válido até 10/01/2027", quando a loja expira saldo. */
  validUntil?: string;
  /** Itens da compra (`details` da integração). */
  items: HistoryItem[];
}

export interface HistoryMonth {
  /** `2026-10`. */
  key: string;
  /** "Outubro de 2026". */
  label: string;
  entries: HistoryEntry[];
}

export function buildHistory(raw: unknown, passbook: Passbook): HistoryEntry[] {
  const logs = normalizePointLogs(raw, normalizeOptsFromPassbook(passbook));
  const money = passbookCurrency(passbook);
  return logs
    .filter((log) => log.date)
    .map((log, index) => {
      const title = movementActionLabel(log, passbook);
      const until = resolveLogValidUntil(log, passbook);
      const isRegistration = title === 'Cadastro' && log.value === 0;
      return {
        id: log._id ?? `${log.date}-${index}`,
        date: log.date,
        title: isRegistration ? 'Você entrou no programa' : title,
        delta: isRegistration ? '' : formatMovementDelta(log, passbook),
        direction: log.value > 0 ? 'in' : log.value < 0 ? 'out' : 'neutral',
        store: log.loja?.trim() || undefined,
        price: log.price ? currency(log.price, money) : undefined,
        validUntil: until && log.value > 0 ? formatMovementValidUntilShort(new Date(until)) : undefined,
        items: (log.products ?? []).flatMap((product): HistoryItem[] => {
          const name = (product.name ?? product.nome ?? '').trim();
          const sku = product.sku != null ? String(product.sku).trim() : '';
          if (!name && !sku) return [];
          const quantity = product.count ?? product.qtde ?? product.qty;
          return [
            {
              sku: sku || undefined,
              name: name || `SKU ${sku}`,
              quantity: quantity && quantity > 0 ? quantity : undefined,
              price: product.price ? currency(product.price, money) : undefined,
            },
          ];
        }),
      } satisfies HistoryEntry;
    });
}

const MONTH = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/** Mais recente primeiro, um grupo por mês (a lista já vem ordenada). */
export function groupByMonth(entries: HistoryEntry[]): HistoryMonth[] {
  const months: HistoryMonth[] = [];
  for (const entry of entries) {
    const date = new Date(entry.date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    let month = months[months.length - 1];
    if (!month || month.key !== key) {
      const label = MONTH.format(date);
      month = { key, label: label.charAt(0).toUpperCase() + label.slice(1), entries: [] };
      months.push(month);
    }
    month.entries.push(entry);
  }
  return months;
}

/** Saldo do topo no vocabulário da loja. */
export function formatBalance(balance: number, passbook: Passbook): string {
  const program = movementProgram(passbook);
  if (program === 'cashback' || program === 'balance') return currency(balance, passbookCurrency(passbook));
  if (program === 'points') return `${decimal(balance, Number.isInteger(balance) ? 0 : 2)} ${balance === 1 ? 'ponto' : 'pontos'}`;
  const stamps = Math.round(balance);
  return `${stamps} ${stamps === 1 ? 'selo' : 'selos'}`;
}
